/*
  File:        BookingDetailPage.tsx
  Path:        /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/BookingDetailPage.tsx
  Description:
    Phase 8.7 (P1): Booking detail page (phone-first).
    - Route: /dashboard/bookings/:bookingId
    - Data source:
        1) Prefer navigation state (booking object passed from BookingsPage)
        2) Fallback: reload GET /api/v1/dashboard/bookings and find by booking_id
    - No backend changes. No mutations.

  Author: AQUORIX Team
  Created: 2026-02-27
  Version: 0.3.4

  Change Log (append-only):
    - 2026-02-27 - v0.1.0:
      - Initial detail page scaffold + fallback loader
    - 2026-02-27 - v0.2.0:
      - Phone-first sections + deterministic display helpers
    - 2026-02-27 - v0.3.0:
      - Wire Actions: Approve + Reject buttons call backend endpoints via apiFetch (POST approve/reject)
      - Add actionState to prevent double-submit and show deterministic error banner on failure
      - Post-approve behavior: reset action state and navigate back to /dashboard/bookings for clean re-entry (no hard reload)
    - 2026-06-18 - v0.3.1:
      - Hide hold expiry from operator-facing payment detail once booking is paid or operationally confirmed.
    - 2026-06-18 - v0.3.2:
      - Replace ambiguous Approve/Reject action row with state-aware Booking Detail actions.
    - 2026-06-20 - v0.3.4:
      - Wire Confirm Seat to operational-only /confirm endpoint.
      - Prevent Confirm Seat from launching Stripe Checkout.
      - Prefer participant/headcount truth for diver count display.
*/

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../../utils/api";

type ApiBooking = {
  booking_id: string;
  booking_status: string;

  payment_status: string;
  payment_method?: string | null;
  refund_status?: string | null;
  payment_currency: string;
  payment_amount: string;
  payment_amount_minor?: string | null;

  hold_expires_at: string | null;
  paid_at: string | null;

  requires_manual_review_derived: boolean;
  manual_review_required?: boolean;
  manual_review_reason: string | null;

  ui_status: string;

  headcount: number;
  total_pax?: number | null;
  item_count?: number | null;

  guest_name: string;
  guest_email: string;
  guest_phone: string | null;

  special_requests: string | null;
  source: string;

  created_at: string;
  updated_at: string;

  session_id: string;
  itinerary_id: string | null;

  dive_datetime_local: string;
  session_date: string;
  start_time: string;
  site_name: string;

  itinerary_title: string;
  itinerary_date?: string | null;
  itinerary_slot?: string | null;
  itinerary_type?: string | null;
  itinerary_location_type?: string | null;

  first_session_at_local?: string | null;
  last_session_at_local?: string | null;
  sites_summary?: string | null;
  booking_items_summary?: string | null;

  roster?: string[];
  participants?: Array<{
    participant_id: number | string;
    participant_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    is_minor?: boolean;
    guardian_name?: string | null;
  }>;

  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;

  stripe_charge_currency: string | null;
  stripe_charge_amount_minor: string | null;

  fx_rate_estimate: string | null;
  fx_rate_source: string | null;
};

type ApiResponse = {
  ok: boolean;
  status: string;
  operator_id: string;
  week: { start: string; end: string };
  bookings: ApiBooking[];
};

type LoadState =
  | { status: "idle" | "loading" }
  | { status: "not_found" }
  | { status: "error"; message: string }
  | { status: "ok"; booking: ApiBooking; operatorId?: string };

function getLocalYmd(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function getLocalIsoDow(d: Date) {
  // JS: 0=Sun..6=Sat -> ISO: 1=Mon..7=Sun
  const js = d.getDay();
  return js === 0 ? 7 : js;
}

function getLocalWeekStartYmd(d: Date) {
  const isoDow = getLocalIsoDow(d); // 1..7
  const monday = new Date(d);
  monday.setDate(d.getDate() - (isoDow - 1));
  return getLocalYmd(monday);
}

function formatMoney(currency: string, amount: string) {
  const cur = currency || "—";
  const amt = amount || "—";
  return `${cur} ${amt}`;
}

function labelize(s?: string | null) {
  if (!s) return "—";
  return String(s).replace(/_/g, " ");
}

function formatSettlementMethod(method?: string | null) {
  if (!method) return "Not recorded";
  if (method === "cash") return "Cash at Register";
  if (method === "card_terminal") return "Card on Terminal";
  if (method === "hold") return "Hold";
  return labelize(method);
}

function formatIsoForDisplay(s?: string | null) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

function getDiverCount(booking: ApiBooking): number | string {
  if (Array.isArray(booking.participants) && booking.participants.length > 0) {
    return booking.participants.length;
  }

  if (Number.isFinite(Number(booking.headcount))) {
    return Number(booking.headcount);
  }

  if (Number.isFinite(Number(booking.total_pax))) {
    return Number(booking.total_pax);
  }

  return "—";
}

function getActionContext(booking: ApiBooking) {
  const uiStatus = String(booking.ui_status || "").toLowerCase();
  const bookingStatus = String(booking.booking_status || "").toLowerCase();
  const paymentStatus = String(booking.payment_status || "").toLowerCase();

  const refundStatus = String(booking.refund_status || "").toLowerCase();

  if (
    bookingStatus === "cancelled" &&
    paymentStatus === "paid" &&
    refundStatus !== "refunded"
  ) {
    return {
      title: "Refund decision",
      description: "Booking is cancelled and payment is still held. Issue refund or open billing.",
      actions: ["issue_refund", "billing"]
    };
  }

  if (bookingStatus === "cancelled" || uiStatus === "cancelled" || uiStatus === "refunded") {
    return {
      title: "Archived booking",
      description: "This booking is terminal. Re-booking requires a new booking.",
      actions: ["billing"]
    };
  }

  if (uiStatus === "paid_manual_review" || (bookingStatus === "pending" && paymentStatus === "paid")) {
    return {
      title: "Decide the seat",
      description: `Payment received (${booking.payment_currency || "JOD"} ${booking.payment_amount || "—"}). Seat is not yet operationally confirmed — confirm only if the seat still exists.`,
      actions: ["payment_recorded", "confirm_seat", "reject_refund", "billing"]
    };
  }

  if (bookingStatus === "confirmed" && paymentStatus === "paid") {
    return {
      title: "Actions",
      description: "Paid and confirmed. No approval needed.",
      actions: ["change_sessions", "cancel_booking", "billing"]
    };
  }

  if (bookingStatus === "confirmed" && paymentStatus === "unpaid") {
    return {
      title: "Actions",
      description: "Confirmed on trust. Payment is still outstanding.",
      actions: ["collect_payment", "change_sessions", "cancel_booking", "billing"]
    };
  }

  if (bookingStatus === "pending" && paymentStatus === "unpaid") {
    return {
      title: "Actions",
      description: "Awaiting payment or operator decision.",
      actions: ["collect_payment", "confirm_comp", "cancel_booking", "billing"]
    };
  }

  return {
    title: "Actions",
    description: "No additional action is currently available for this state.",
    actions: ["billing"]
  };
}

export default function BookingDetailPage() {
  const nav = useNavigate();
  const { bookingId } = useParams();
  const location = useLocation() as any;

  const navBooking = location?.state?.booking as ApiBooking | undefined;
  const navWeekStart = location?.state?.weekStart as string | undefined;

  const [state, setState] = useState<LoadState>(() => {
    if (navBooking) return { status: "ok", booking: navBooking };
    return { status: "idle" };
  });

  const [actionState, setActionState] = useState<{ status: "idle" | "working" | "ok" | "error"; message?: string }>({
    status: "idle",
  });

  useEffect(() => {
    let isMounted = true;

    async function loadFallback() {
      if (navBooking) return; // already have it
      if (!bookingId) return;

      setState({ status: "loading" });

      try {
        const weekStart = navWeekStart || getLocalWeekStartYmd(new Date());
        const res = await apiFetch(`/api/v1/dashboard/bookings?week_start=${encodeURIComponent(weekStart)}`, { method: "GET" });
        const json = (await res.json().catch(() => ({}))) as any;

        if (!res.ok || json?.ok === false) {
          const msg = json?.message || `HTTP ${res.status}`;
          throw new Error(msg);
        }

        const data = json as ApiResponse;
        const found = (data.bookings || []).find((b) => String(b.booking_id) === String(bookingId));

        if (!found) {
          if (isMounted) setState({ status: "not_found" });
          return;
        }

        if (isMounted) setState({ status: "ok", booking: found, operatorId: data.operator_id });
      } catch (err: any) {
        if (isMounted) setState({ status: "error", message: err?.message || "Load failed" });
      }
    }

    loadFallback();
    return () => {
      isMounted = false;
    };
 }, [bookingId, navBooking, navWeekStart]);

  const booking = state.status === "ok" ? state.booking : null;
  const actionContext = booking ? getActionContext(booking) : null;
  const shouldShowHoldExpires =
    Boolean(booking?.hold_expires_at) &&
    booking?.booking_status !== "confirmed" &&
    booking?.payment_status !== "paid";

  async function handleConfirmSeat() {
    if (!bookingId) return;

    setActionState({ status: "working" });

    try {
      const res = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(String(bookingId))}/confirm`, {
        method: "POST",
      });
      const json = (await res.json().catch(() => ({}))) as any;

      if (!res.ok || json?.ok === false) {
        const msg = json?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      const updatedBooking = json?.booking || {};

      setState((prev) => {
        if (prev.status !== "ok") return prev;

        return {
          ...prev,
          booking: {
            ...prev.booking,
            ...updatedBooking,
            booking_status: updatedBooking.booking_status || "confirmed",
            payment_status: updatedBooking.payment_status || prev.booking.payment_status,
            ui_status:
              updatedBooking.ui_status ||
              (String(updatedBooking.payment_status || prev.booking.payment_status).toLowerCase() === "paid"
                ? "paid_confirmed"
                : "confirmed_unpaid"),
          },
        };
      });

      setActionState({
        status: "ok",
        message: "Seat confirmed. Operational status is now confirmed. Payment truth was not changed.",
      });
    } catch (err: any) {
      setActionState({ status: "error", message: err?.message || "Confirm seat failed" });
    }
  }

  async function handleCancelBooking() {
    if (!bookingId || !booking) return;

    const yes = window.confirm(
      `Cancel booking #${String(booking.booking_id)}?\n\nThis releases the operational seat. Payment/refund truth is not changed by this action.`
    );

    if (!yes) return;

    setActionState({ status: "working" });

    try {
      const res = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(String(bookingId))}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cancellation_reason: `Cancelled from Booking Detail for booking #${String(booking.booking_id)}`
        })
      });

      const json = (await res.json().catch(() => ({}))) as any;

      if (!res.ok || json?.ok === false) {
        const msg = json?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      setActionState({ status: "idle" });
      nav("/dashboard/bookings");
    } catch (err: any) {
      setActionState({ status: "error", message: err?.message || "Cancel failed" });
    }
  }

    async function handleIssueRefund() {
    if (!bookingId || !booking) return;

    const yes = window.confirm(
      `Issue refund for booking #${String(booking.booking_id)}?\n\nThis records the refund axis. Booking/payment history remains preserved.`
    );

    if (!yes) return;

    setActionState({ status: "working" });

    try {
      const res = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(String(bookingId))}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reason: `Refund issued from Booking Detail for booking #${String(booking.booking_id)}`
        })
      });

      const json = (await res.json().catch(() => ({}))) as any;

      if (!res.ok || json?.ok === false) {
        const msg = json?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      setState({
        status: "ok",
        operatorId: state.status === "ok" ? state.operatorId : undefined,
        booking: {
          ...booking,
          ...(json?.booking || {}),
          refund_status: json?.booking?.refund_status || "refunded",
          ui_status: "refunded",
        },
      });

      setActionState({
        status: "ok",
        message: "Refund recorded. Operational cancellation and payment history remain preserved.",
      });
    } catch (err: any) {
      setActionState({ status: "error", message: err?.message || "Refund failed" });
    }
  }

  const sections = useMemo(() => {
    if (!booking) return null;

    const uiStatus = String(booking.ui_status || "").toLowerCase();
    const needsReview = uiStatus === "paid_manual_review";

    return {
      header: {
        title: booking.guest_name || "Guest",
        subtitle: `${booking.booking_items_summary || "1 session booked"} • ${getDiverCount(booking)} divers`,
        sites: booking.sites_summary || booking.site_name || "—",
        window:
          booking.first_session_at_local && booking.last_session_at_local
            ? `${formatIsoForDisplay(booking.first_session_at_local)} → ${formatIsoForDisplay(booking.last_session_at_local)}`
            : `${booking.session_date || "—"} ${booking.start_time || ""}`,
        metaRightTop: `#${booking.booking_id}`,
        metaRightBottom: (booking.source || "—").toUpperCase(),
      },
      statusRow: {
        ui: labelize(booking.ui_status).toUpperCase(),
        booking: (booking.booking_status || "—").toUpperCase(),
        payment: (booking.payment_status || "—").toUpperCase(),
        needsReview,
        reviewReason: booking.manual_review_reason,
      },
    };
  }, [booking]);

  return (
    <div style={styles.page}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <button onClick={() => nav(-1)} style={styles.backBtn}>
          ← Back
        </button>
        <h1 style={styles.h1}>Booking</h1>
      </div>

      {state.status === "loading" || state.status === "idle" ? <div style={styles.card}>Loading booking…</div> : null}

      {state.status === "error" ? (
        <div style={styles.bannerError}>
          <strong>Booking load failed.</strong>
          <div style={{ marginTop: 6 }}>{state.message}</div>
        </div>
      ) : null}

      {state.status === "not_found" ? (
        <div style={styles.card}>
          Booking not found in the currently loaded week. Go back to Bookings and select the correct week.
        </div>
      ) : null}

      {state.status === "ok" && booking && sections ? (
        <>
          {/* Header Card */}
          <div style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 950, marginBottom: 6 }}>{sections.header.title}</div>
                <div style={{ fontSize: 13, opacity: 0.85 }}>{sections.header.subtitle}</div>
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
                  {sections.header.sites}
                </div>

                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                  {sections.header.window}
                </div>
              </div>

              <div style={{ textAlign: "right", fontSize: 12, opacity: 0.85 }}>
                <div style={{ fontWeight: 900 }}>{sections.header.metaRightTop}</div>
                <div style={{ marginTop: 6 }}>{sections.header.metaRightBottom}</div>
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
              <span style={pill("neutral")}>{sections.statusRow.ui}</span>
              <span style={pill("neutral")}>BOOKING: {sections.statusRow.booking}</span>
              <span style={pill(sections.statusRow.payment === "PAID" ? "ok" : "warn")}>PAY: {sections.statusRow.payment}</span>
              {sections.statusRow.needsReview ? <span style={pill("warn")}>NEEDS REVIEW</span> : null}
              {sections.statusRow.reviewReason ? <span style={pill("warn")}>REASON: {labelize(sections.statusRow.reviewReason)}</span> : null}
            </div>
          </div>

          {/* Booking Scope */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Booking Scope</div>
            <Row k="Sessions" v={booking.booking_items_summary || "—"} />
            <Row k="Divers" v={String(getDiverCount(booking))} />
            <Row
              k="Booking Window"
              v={
                booking.first_session_at_local && booking.last_session_at_local
                  ? `${formatIsoForDisplay(booking.first_session_at_local)} → ${formatIsoForDisplay(booking.last_session_at_local)}`
                  : "—"
              }
            />
            <Row k="Sites" v={booking.sites_summary || booking.site_name || "—"} />
            <Row k="Primary Session ID" v={booking.session_id || "—"} />
            <Row k="Primary Itinerary ID" v={booking.itinerary_id || "—"} />
            <Row k="Primary Itinerary Title" v={booking.itinerary_title || "—"} />
          </div>

          {/* Guest */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Guest</div>
            <Row k="Name" v={booking.guest_name || "—"} />
            <Row k="Email" v={booking.guest_email || "—"} mono />
            <Row k="Phone" v={booking.guest_phone || "—"} mono />
            <Row k="Headcount" v={String(booking.headcount ?? "—")} />
            <Row k="Requests" v={booking.special_requests || "—"} />
          </div>

                    {/* Diver Manifest */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Diver Manifest</div>

            {Array.isArray(booking.participants) && booking.participants.length > 0 ? (
              <div style={{ display: "grid", gap: 8 }}>
                {booking.participants.map((p, index) => (
                  <div
                    key={String(p.participant_id || index)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr auto",
                      gap: 10,
                      alignItems: "start",
                      padding: "8px 0",
                      borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    }}
                  >
                    <div style={{ fontWeight: 900, opacity: 0.65 }}>{index + 1}</div>

                    <div>
                      <div style={{ fontWeight: 900 }}>
                        {p.participant_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unnamed Diver"}
                      </div>

                      {p.is_minor ? (
                        <div style={{ fontSize: 12, opacity: 0.78, marginTop: 3 }}>
                          Minor
                          {p.guardian_name ? ` • Guardian: ${p.guardian_name}` : ""}
                        </div>
                      ) : null}
                    </div>

                    {p.is_minor ? <span style={pill("warn")}>MINOR</span> : null}
                  </div>
                ))}
              </div>
            ) : (
              <Row k="Manifest" v="No participant manifest found" />
            )}
          </div>

          {/* Payment */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Payment</div>
            <Row k="Amount" v={formatMoney(booking.payment_currency, booking.payment_amount)} />
            <Row k="Settlement Method" v={formatSettlementMethod(booking.payment_method)} />
            <Row k="Paid At" v={booking.paid_at || "—"} />
            {shouldShowHoldExpires ? <Row k="Hold Expires" v={booking.hold_expires_at || "—"} /> : null}
            <Row k="Checkout Session" v={booking.stripe_checkout_session_id || "—"} mono />
            <Row k="Payment Intent" v={booking.stripe_payment_intent_id || "—"} mono />
            <Row
              k="Charge"
              v={
                booking.stripe_charge_currency && booking.stripe_charge_amount_minor
                  ? `${booking.stripe_charge_currency} ${booking.stripe_charge_amount_minor} (minor)`
                  : "—"
              }
            />
            <Row
              k="FX"
              v={booking.fx_rate_estimate ? `${booking.fx_rate_estimate} (${booking.fx_rate_source || "source unknown"})` : "—"}
            />
          </div>

          {/* Actions */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{actionContext?.title || "Actions"}</div>

            {actionState.status === "ok" ? (
              <div style={{ ...styles.bannerOk, marginBottom: 10 }}>
                <strong>Action complete.</strong>
                <div style={{ marginTop: 6 }}>{actionState.message}</div>
              </div>
            ) : null}

            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
              {actionContext?.description || "Actions are based on booking and payment truth."}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                
                {actionContext?.actions.includes("payment_recorded") ? (
                <button
                  disabled={true}
                  style={styles.actionBtnDisabled}
                  title="Payment has already been recorded."
                >
                  ✓ Payment recorded
                </button>
              ) : null}
              {actionContext?.actions.includes("collect_payment") ? (
                <button
                  onClick={() => nav(`/dashboard/billing/${booking.booking_id}`)}
                  disabled={actionState.status === "working"}
                  style={actionState.status === "working" ? styles.actionBtnDisabled : styles.actionBtn}
                >
                  Collect Payment
                </button>
              ) : null}

              {actionContext?.actions.includes("confirm_seat") ? (
                <button
                  onClick={handleConfirmSeat}
                  disabled={actionState.status === "working"}
                  style={actionState.status === "working" ? styles.actionBtnDisabled : styles.actionBtn}
                >
                  Confirm Seat
                </button>
              ) : null}

              {actionContext?.actions.includes("confirm_comp") ? (
                <button
                  disabled={true}
                  style={styles.actionBtnDisabled}
                  title="Trust-confirm UI wiring is next."
                >
                  Confirm Seat (Comp)
                </button>
              ) : null}

              {actionContext?.actions.includes("change_sessions") ? (
                <button
                  disabled={true}
                  style={styles.actionBtnDisabled}
                  title="Change Sessions flow is next."
                >
                  Change Sessions
                </button>
              ) : null}

              {actionContext?.actions.includes("cancel_booking") ? (
                <button
                  onClick={handleCancelBooking}
                  disabled={actionState.status === "working"}
                  style={actionState.status === "working" ? styles.actionBtnDisabled : styles.actionBtnDanger}
                  title="Cancel booking and release operational inventory."
                >
                  Cancel Booking
                </button>
              ) : null}

              {actionContext?.actions.includes("reject_refund") ? (
                <button
                  disabled={true}
                  style={styles.actionBtnDisabled}
                  title="Reject & refund requires cancellation/refund workflow."
                >
                  Reject & Refund
                </button>
              ) : null}

              {actionContext?.actions.includes("issue_refund") ? (
                <button
                  onClick={handleIssueRefund}
                  disabled={actionState.status === "working"}
                  style={actionState.status === "working" ? styles.actionBtnDisabled : styles.actionBtnDanger}
                  title="Issue refund for a cancelled paid booking."
                >
                  Issue Refund
                </button>
              ) : null}

              {actionContext?.actions.includes("billing") ? (
                <button
                  onClick={() => nav(`/dashboard/billing/${booking.booking_id}`)}
                  disabled={actionState.status === "working"}
                  style={actionState.status === "working" ? styles.actionBtnDisabled : styles.actionBtn}
                >
                  Open Billing
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 10 }}>
            Created: {booking.created_at} • Updated: {booking.updated_at} {state.operatorId ? `• Operator #${state.operatorId}` : ""}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Row(props: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={styles.row}>
      <div style={styles.k}>{props.k}</div>
      <div style={{ ...styles.v, ...(props.mono ? styles.mono : null) }}>{props.v}</div>
    </div>
  );
}

function pill(kind: "ok" | "warn" | "danger" | "neutral"): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  if (kind === "ok") return { ...base, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)" };
  if (kind === "warn") return { ...base, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" };
  if (kind === "danger") return { ...base, background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.30)" };
  return { ...base, background: "rgba(0,0,0,0.04)" };
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 16, maxWidth: 900 },
  h1: { fontSize: 22, fontWeight: 950, margin: 0 },

  backBtn: {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.14)",
    background: "rgba(0,0,0,0.02)",
    cursor: "pointer",
    fontWeight: 900,
  },

  card: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 14,
    padding: 14,
    background: "#fff",
    marginBottom: 12,
  },

  bannerOk: {
    border: "1px solid rgba(16, 185, 129, 0.35)",
    background: "rgba(16, 185, 129, 0.08)",
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },

  sectionTitle: { fontSize: 14, fontWeight: 950, marginBottom: 10 },

  row: {
    display: "flex",
    gap: 12,
    padding: "8px 0",
    borderTop: "1px solid rgba(0,0,0,0.06)",
  },
  k: { width: 140, fontSize: 12, fontWeight: 900, opacity: 0.75 },
  v: { flex: 1, fontSize: 12, fontWeight: 700, opacity: 0.9, wordBreak: "break-word" },
  mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" },

  actionBtn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(0,0,0,0.02)",
    cursor: "pointer",
    fontWeight: 950,
  },

  actionBtnDanger: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(220,38,38,0.30)",
    background: "rgba(220,38,38,0.08)",
    cursor: "pointer",
    fontWeight: 950,
  },

  actionBtnDisabled: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "rgba(0,0,0,0.04)",
    cursor: "not-allowed",
    fontWeight: 950,
    opacity: 0.65,
  },
};

